import { extractImageFrames, clampTo, withDraft480p } from '../../tools';
import { withDraftTask } from '../../draft';
import type { baiduModelConfig } from '../types';

const seedance20Config: baiduModelConfig = {
        apiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks',
        pollApiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks/{id}',
        statusMapping: {
            taskIdPath: 'id',
            statePath: 'status',
            success: ['succeeded'],
            failed: ['failed', 'cancelled', 'expired'],
            processing: ['queued', 'running'],
            videoUrlPath: 'content.video_url',
            errorPath: 'error.message',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'doubao-seedance-2-0-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'doubao-seedance-2-0-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                        {
                            type: "image_url",
                            image_url: { url: lastImage || firstImage },
                            role: "last_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'doubao-seedance-2-0-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        ...images.map(url => ({
                            type: "image_url",
                            image_url: { "url": url },
                            role: "reference_image"
                        })),
                        ...videos.map(url => ({
                            type: "video_url",
                            video_url: { "url": url },
                            role: "reference_video"
                        })),
                        ...audios.map(url => ({
                            type: "audio_url",
                            audio_url: { "url": url },
                            role: "reference_audio"
                        })),
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'doubao-seedance-2-0-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt,
                        }
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            }
        },
};

const seedance20FastConfig: baiduModelConfig = {
        apiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks',
        pollApiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks/{id}',
        statusMapping: {
            taskIdPath: 'id',
            statePath: 'status',
            success: ['succeeded'],
            failed: ['failed', 'cancelled', 'expired'],
            processing: ['queued', 'running'],
            videoUrlPath: 'content.video_url',
            errorPath: 'error.message',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'doubao-seedance-2-0-fast-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'doubao-seedance-2-0-fast-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                        {
                            type: "image_url",
                            image_url: { url: lastImage || firstImage },
                            role: "last_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'doubao-seedance-2-0-fast-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        ...images.map(url => ({
                            type: "image_url",
                            image_url: { "url": url },
                            role: "reference_image"
                        })),
                        ...videos.map(url => ({
                            type: "video_url",
                            video_url: { "url": url },
                            role: "reference_video"
                        })),
                        ...audios.map(url => ({
                            type: "audio_url",
                            audio_url: { "url": url },
                            role: "reference_audio"
                        })),
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'doubao-seedance-2-0-fast-260128',
                    content: [
                        {
                            type: "text",
                            text: prompt,
                        }
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            }
        },
};

const seedance20MiniConfig: baiduModelConfig = {
        apiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks',
        pollApiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks/{id}',
        statusMapping: {
            taskIdPath: 'id',
            statePath: 'status',
            success: ['succeeded'],
            failed: ['failed', 'cancelled', 'expired'],
            processing: ['queued', 'running'],
            videoUrlPath: 'content.video_url',
            errorPath: 'error.message',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'doubao-seedance-2-0-mini-260615',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'doubao-seedance-2-0-mini-260615',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                        {
                            type: "image_url",
                            image_url: { url: lastImage || firstImage },
                            role: "last_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'doubao-seedance-2-0-mini-260615',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        ...images.map(url => ({
                            type: "image_url",
                            image_url: { "url": url },
                            role: "reference_image"
                        })),
                        ...videos.map(url => ({
                            type: "video_url",
                            video_url: { "url": url },
                            role: "reference_video"
                        })),
                        ...audios.map(url => ({
                            type: "audio_url",
                            audio_url: { "url": url },
                            role: "reference_audio"
                        })),
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'doubao-seedance-2-0-mini-260615',
                    content: [
                        {
                            type: "text",
                            text: prompt,
                        }
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    return_last_frame: undefined,
                    execution_expires_after: undefined,
                    safety_identifiernew: undefined,
                    priority: undefined,
                };
            }
        },
};

const seedance25Config: baiduModelConfig = {
        apiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks',
        pollApiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks/{id}',
        statusMapping: {
            taskIdPath: 'id',
            statePath: 'status',
            success: ['succeeded'],
            failed: ['failed', 'cancelled', 'expired'],
            processing: ['queued', 'running'],
            videoUrlPath: 'content.video_url',
            errorPath: 'error.message',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 30);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 10);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 10);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'doubao-seedance-2-5-260628',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p'),
                    ratio: 'adaptive',
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    output_format: clampTo(
                        (typeof providerOptions?.output_format === 'string' ? providerOptions.output_format : 'mp4'),
                        ['mp4', 'mov'] as const,
                        'mp4',
                    ),
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    execution_expires_after: undefined,
                    priority: undefined,
                    return_last_frame: undefined,
                    safety_identifiernew: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'doubao-seedance-2-5-260628',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: { url: firstImage },
                            role: "first_frame"
                        },
                        {
                            type: "image_url",
                            image_url: { url: lastImage || firstImage },
                            role: "last_frame"
                        },
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p'),
                    ratio: 'adaptive',
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    output_format: clampTo(
                        (typeof providerOptions?.output_format === 'string' ? providerOptions.output_format : 'mp4'),
                        ['mp4', 'mov'] as const,
                        'mp4',
                    ),
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    execution_expires_after: undefined,
                    priority: undefined,
                    return_last_frame: undefined,
                    safety_identifiernew: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'doubao-seedance-2-5-260628',
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        ...images.map(url => ({
                            type: "image_url",
                            image_url: { "url": url },
                            role: "reference_image"
                        })),
                        ...videos.map(url => ({
                            type: "video_url",
                            video_url: { "url": url },
                            role: "reference_video"
                        })),
                        ...audios.map(url => ({
                            type: "audio_url",
                            audio_url: { "url": url },
                            role: "reference_audio"
                        })),
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    output_format: clampTo(
                        (typeof providerOptions?.output_format === 'string' ? providerOptions.output_format : 'mp4'),
                        ['mp4', 'mov'] as const,
                        'mp4',
                    ),
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    omni_reference_task_type: 'auto',
                    callback_url: undefined,
                    execution_expires_after: undefined,
                    priority: undefined,
                    return_last_frame: undefined,
                    safety_identifiernew: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'doubao-seedance-2-5-260628',
                    content: [
                        {
                            type: "text",
                            text: prompt,
                        }
                    ],
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p'),
                    ratio: aspectRatio === 'match' ? 'adaptive' : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                    // 可选参数
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    output_format: clampTo(
                        (typeof providerOptions?.output_format === 'string' ? providerOptions.output_format : 'mp4'),
                        ['mp4', 'mov'] as const,
                        'mp4',
                    ),
                    ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                    // system
                    callback_url: undefined,
                    execution_expires_after: undefined,
                    priority: undefined,
                    return_last_frame: undefined,
                    safety_identifiernew: undefined,
                };
            }
        },
};

const seedance25V2vConfig: baiduModelConfig = {
        apiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks',
        pollApiPath: '/v3/aigc/seedance/api/v3/contents/generations/tasks/{id}',
        statusMapping: {
            taskIdPath: 'id',
            statePath: 'status',
            success: ['succeeded'],
            failed: ['failed', 'cancelled', 'expired'],
            processing: ['queued', 'running'],
            videoUrlPath: 'content.video_url',
            errorPath: 'error.message',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, audioUri, audioUris, quality, providerOptions } = params;
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 30);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 10);
            // 视频编辑模式
            return {
                model: 'doubao-seedance-2-5-260628',
                content: [
                    {
                        type: "text",
                        text: prompt
                    },
                    {
                        type: "video_url",
                        video_url: { url: videoUri },
                        role: "reference_video"
                    },
                    ...images.map(url => ({
                        type: "image_url",
                        image_url: { url },
                        role: "reference_image"
                    })),
                    ...audios.map(url => ({
                        type: "audio_url",
                        audio_url: { url },
                        role: "reference_audio"
                    })),
                ],
                omni_reference_task_type: 'edit',
                resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p'),
                ratio: 'adaptive',
                duration: -1,
                // 可选参数
                generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                output_format: clampTo(
                    (typeof providerOptions?.output_format === 'string' ? providerOptions.output_format : 'mp4'),
                    ['mp4', 'mov'] as const,
                    'mp4',
                ),
                ...(typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search === true ? { tools: [{ "type": "web_search" }] } : { tools: undefined } : { tools: undefined }),
                // system
                callback_url: undefined,
                execution_expires_after: undefined,
                priority: undefined,
                return_last_frame: undefined,
                safety_identifiernew: undefined,
            };
        },
};

/** 不支持审核：直传 URL，不走 CreateAsset */
function withoutMaterialReview(config: baiduModelConfig): baiduModelConfig {
    return { ...config, enableAigcMaterialReview: false };
}

/** 支持审核：CreateAsset → asset:// */
function withMaterialReview(config: baiduModelConfig): baiduModelConfig {
    return { ...config, enableAigcMaterialReview: true };
}

export const seedanceConfigs: Record<string, baiduModelConfig> = {
    // 不支持审核
    'seedance-2.0-api': withoutMaterialReview(seedance20Config),
    'seedance-2.0-fast-api': withoutMaterialReview(seedance20FastConfig),
    'seedance-2.0-mini-api': withoutMaterialReview(seedance20MiniConfig),
    // 支持审核
    'seedance-2.0-api-real': withMaterialReview(seedance20Config),
    'seedance-2.0-fast-api-real': withMaterialReview(seedance20FastConfig),
    'seedance-2.0-mini-api-real': withMaterialReview(seedance20MiniConfig),
    // 同时承接样片转正式（providerOptions._draftTask）
    'seedance-2.5-api-real': withDraftTask(withMaterialReview(seedance25Config)),
    'seedance-2.5-api-v2v-real': withMaterialReview(seedance25V2vConfig),
    'seedance-2.5-api-draft-real': withDraft480p(withMaterialReview(seedance25Config)),
};